// @ts-ignore - React import for JSX compatibility
import React from 'react';
import { useState, useMemo, useEffect, type ReactNode, useRef } from 'react';
import { getCookie, setCookie, clearListCookies } from '../helpers/cookies';
import { Loading } from '../../components';
import * as api from '../../services';
import { offlineStorage } from '../../services';
import {
  GeneralContext,
  type UserData,
  type Auth0User,
  type ClientData,
  type CountryOption,
  type CountryDdiOption,
} from './GeneralContext.context';

const getUniqueArrayBy = <T extends Record<string, any>>(
  arr: T[],
  key: keyof T
): T[] => {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
};

// Safe JSON parse helper
const safeParse = <T = any,>(value: string | null | undefined): T | null => {
  if (!value || value === 'null' || value === 'undefined' || value === '') {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch (err) {
    console.warn('⚠️ Failed to parse JSON:', value, err);
    return null;
  }
};

interface GeneralProviderProps {
  children: ReactNode;
}

type AmosLoginData = {
  isRegisteredAmos: boolean;
  clientToken?: string;
  clientRefreshToken?: string;
  clientId?: string;
  clientCode?: string;
  user?: UserData;
};

type AmosB2BLoginData = {
  isRegisteredB2B: boolean;
  auth_code?: string;
  clientToken?: string;
  clientRefreshToken?: string;
  clientId?: string;
  clientCode?: string;
  user?: UserData;
};

type AmosSessionData = AmosLoginData & {
  clientToken: string;
  clientRefreshToken: string;
  clientId: string;
};

const hasAmosSessionData = (data: AmosLoginData): data is AmosSessionData => {
  return (
    data.isRegisteredAmos === true &&
    typeof data.clientToken === 'string' &&
    typeof data.clientRefreshToken === 'string' &&
    typeof data.clientId === 'string'
  );
};

export function GeneralProvider({ children }: GeneralProviderProps) {
  const initialUserData = safeParse<UserData>(getCookie('userAuth'));
  const initialAuth0User = safeParse<Auth0User>(
    decodeURIComponent(getCookie('user') || '')
  );

  // Store email in localStorage whenever we have it from cookies (for offline access)
  if (initialUserData?.email) {
    localStorage.setItem('lastUserEmail', initialUserData.email);
  } else if (initialAuth0User?.userInfo?.email) {
    localStorage.setItem('lastUserEmail', initialAuth0User.userInfo.email);
  }

  const [userData, setUserData] = useState<UserData | null>(initialUserData);
  const [auth0User, setAuth0User] = useState<Auth0User | null>(
    initialAuth0User
  );
  const [refreshClientAndDependents, setRefreshClientAndDependents] =
    useState(false);
  const [amosToken, setAmosToken] = useState<string | null>(
    getCookie('amosToken')
  );
  const [amosRefreshToken, setAmosRefreshToken] = useState<string | null>(
    getCookie('amosRefreshToken')
  );
  const [amosClient, setAmosClient] = useState<string | null>(
    getCookie('amosClient')
  );
  const [country, setCountry] = useState<CountryOption[]>([]);
  const [countryDdi, setCountryDdi] = useState<CountryDdiOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ispopup, setIspopup] = useState(false);
  const [client, setClient] = useState<ClientData | null>(null);
  const [dependents, setDependents] = useState<any[] | null>(null);
  const [adults, setAdults] = useState<any>(null);
  const [adultClientId, setAdultClientId] = useState<string | null>(
    getCookie('amosAdultClientId')
  );
  const [adultClientToken, setAdultClientToken] = useState<string | null>(
    getCookie('amosAdultClientToken')
  );
  const [adultClientCode, setAdultClientCode] = useState<string | null>(
    getCookie('amosAdultClientCode')
  );
  const [adultRefreshToken, setAdultRefreshToken] = useState<string | null>(
    getCookie('amosAdultRefreshToken')
  );

  const isLogged = useMemo(() => {
    return !!auth0User;
  }, [auth0User]);

  // In-flight promise refs — prevent concurrent duplicate token requests
  const loginAmosInFlight = useRef<Promise<void> | null>(null);
  const refreshTokenInFlight = useRef<Promise<void> | null>(null);

  // Cleanup old IndexedDB data on app initialization
  useEffect(() => {
    const cleanupOldData = async () => {
      try {
        await offlineStorage.clearOldData(); // Default: 7 days
      } catch (err) {
        console.error('❌ Error during IndexedDB cleanup:', err);
      }
    };

    cleanupOldData();
  }, []);

  // Load user auth from IndexedDB when cookies are missing (offline scenario)
  // OR store in IndexedDB when cookies are present (for future offline use)
  useEffect(() => {
    const handleAuthPersistence = async () => {
      // Check if we're in a logout flow - but ONLY block when ONLINE
      // When OFFLINE, always trust IndexedDB even if logout flag is set
      const isLoggingOut = sessionStorage.getItem('isLoggingOut');

      if (isLoggingOut === 'true') {
        if (navigator.onLine) {
          sessionStorage.removeItem('isLoggingOut'); // Clear flag
          return; // Don't restore auth during active online logout
        } else {
          sessionStorage.removeItem('isLoggingOut'); // Clear stale flag
          // Continue to restore auth when offline
        }
      }

      const userEmail =
        userData?.email || localStorage.getItem('lastUserEmail');

      if (!userEmail) {
        console.log('⚠️ No user email found - cannot manage auth persistence');
        return;
      }

      // Case 1: User IS logged in via cookies - store in IndexedDB for future
      if (auth0User && userData) {
        try {
          await offlineStorage.storeUserAuth(userEmail, userData, auth0User);
        } catch (err) {
          console.error('❌ Error caching auth in IndexedDB:', err);
        }
      }
      // Case 2: User NOT fully logged in via cookies - try to load from IndexedDB
      // Check if EITHER auth0User OR userData is missing (they can expire independently)
      else if (!auth0User || !userData) {
        try {
          const cachedAuth = await offlineStorage.getUserAuth(userEmail);
          if (cachedAuth && cachedAuth.auth0User && cachedAuth.userData) {
            // Only set the missing pieces
            if (!auth0User) {
              setAuth0User(cachedAuth.auth0User);
            }
            if (!userData) {
              setUserData(cachedAuth.userData);
            }
          } else {
            console.log('⚠️ No cached auth found in IndexedDB');
          }
        } catch (err) {
          console.error('❌ Error loading auth from IndexedDB:', err);
        }
      }
    };

    handleAuthPersistence();
  }, [auth0User, userData]);

  const clientCodeSub = userData?.sub ? userData.sub.split('|') : null;

  const clientCode = useMemo(
    () => (clientCodeSub ? clientCodeSub[clientCodeSub.length - 1] : null),
    [clientCodeSub]
  );

  const fetchLogin = async (): Promise<AmosLoginData> => {
    try {
      const response = await api.apiService.amos.amosLogin();
      if (typeof response === 'string') {
        return { isRegisteredAmos: false };
      }
      return response.data;
    } catch (_err) {
      return {
        isRegisteredAmos: false,
      };
    }
  };

  const fetchLoginB2BAdult = async (): Promise<AmosB2BLoginData> => {
    try {
      const response = await api.apiService.amos.loginB2BAdult();
      if (typeof response === 'string') {
        return { isRegisteredB2B: false };
      }
      return response.data;
    } catch (err) {
      return {
        isRegisteredB2B: false,
      };
    }
  };

  const fetchLoginAdult = async (): Promise<AmosLoginData> => {
    try {
      const response = await api.apiService.amos.amosLoginAdult();
      if (typeof response === 'string') {
        return { isRegisteredAmos: false };
      }
      return response.data;
    } catch (err) {
      console.log(err);
      return {
        isRegisteredAmos: false,
      };
    }
  };

  const updatePrimaryAmosSession = async (
    data: AmosLoginData
  ): Promise<void> => {
    if (hasAmosSessionData(data)) {
      setCookie('amosToken', data.clientToken);
      setCookie('amosRefreshToken', data.clientRefreshToken);
      setCookie('amosClient', data.clientId);
      setAmosToken(data.clientToken);
      setAmosRefreshToken(data.clientRefreshToken);
      setAmosClient(data.clientId);

      // Store token in IndexedDB for offline use
      const userEmail =
        userData?.email || localStorage.getItem('lastUserEmail');
      if (userEmail) {
        try {
          await offlineStorage.storeAmosToken(
            userEmail,
            data.clientToken,
            data.clientRefreshToken,
            data.clientId
          );
        } catch (err) {
          console.error('❌ Error storing AMOS token in IndexedDB:', err);
        }
      }
      return;
    }
    setCookie('amosToken', '');
    setCookie('amosRefreshToken', '');
    setCookie('amosClient', '');
    setAmosToken(null);
    setAmosRefreshToken(null);
    setAmosClient(null);
  };

  const updateAdultAmosSession = (adultData: AmosLoginData): void => {
    if (hasAmosSessionData(adultData)) {
      setCookie('amosAdultClientToken', adultData.clientToken);
      setCookie('amosAdultRefreshToken', adultData.clientRefreshToken);
      setCookie('amosAdultClientId', adultData.clientId);
      if (
        'clientCode' in adultData &&
        typeof adultData.clientCode === 'string'
      ) {
        setCookie('amosAdultClientCode', adultData.clientCode);
        setAdultClientCode(adultData.clientCode);
      }

      setAdultClientId(adultData.clientId);
      setAdultClientToken(adultData.clientToken);
      setAdultRefreshToken(adultData.clientRefreshToken);
      return;
    }
    setCookie('amosAdultClientToken', '');
    setCookie('amosAdultRefreshToken', '');
    setCookie('amosAdultClientId', '');
    setCookie('amosAdultClientCode', '');

    setAdultClientId(null);
    setAdultClientToken(null);
    setAdultClientCode(null);
    setAdultRefreshToken(null);
  };

  const buildMountUser = (data: AmosLoginData): UserData => {
    const baseUser = data.user ?? auth0User?.userInfo ?? {};
    return {
      ...baseUser,
      isRegisteredAmos: data.isRegisteredAmos,
    };
  };

  const persistUserAuthOffline = async (mountUser: UserData): Promise<void> => {
    if (!(mountUser?.email && auth0User)) {
      return;
    }

    try {
      await offlineStorage.storeUserAuth(mountUser.email, mountUser, auth0User);
      // Store email in localStorage for future offline access
      localStorage.setItem('lastUserEmail', mountUser.email);
    } catch (err) {
      console.error('❌ Error storing auth in IndexedDB:', err);
    }
  };

  const loginAmos = async (): Promise<void> => {
    // If a login is already in progress, return the same promise so callers
    // share the result instead of firing parallel requests to Auth0.
    if (loginAmosInFlight.current) {
      return loginAmosInFlight.current;
    }

    const loginPromise: Promise<void> = (async () => {
      try {
        setIsLoading(true);
        if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
          const isConsultationPage =
            window.location.pathname.includes('/consultation');

          if (isConsultationPage) {
            const existingToken = getCookie('amosToken');
            const existingRefresh = getCookie('amosRefreshToken');
            const existingClient = getCookie('amosClient');

            if (
              existingToken &&
              existingToken !== '' &&
              existingToken !== '{}'
            ) {
              setAmosToken(existingToken);
              setAmosRefreshToken(existingRefresh);
              setAmosClient(existingClient);

              const existingAdultToken = getCookie('amosAdultClientToken');
              const existingAdultRefresh = getCookie('amosAdultRefreshToken');
              const existingAdultClientId = getCookie('amosAdultClientId');
              if (existingAdultToken && existingAdultToken !== '') {
                setAdultClientToken(existingAdultToken);
                setAdultRefreshToken(existingAdultRefresh);
                setAdultClientId(existingAdultClientId);
              }

              let mountUser = { isRegisteredAmos: true } as UserData;
              if (auth0User?.userInfo) {
                mountUser = { ...auth0User.userInfo, ...mountUser };
              }
              setCookie('userAuth', JSON.stringify(mountUser));
              setUserData(mountUser);
              setIsLoading(false);
              return;
            }
          }

          try {
            const authCodeResponse = await api.apiService.amos.getAuthCode();
            if (typeof authCodeResponse === 'string') {
              throw new Error('Failed to fetch B2B auth code');
            }

            const b2bData = authCodeResponse.data as AmosB2BLoginData;

            if (b2bData && b2bData.isRegisteredB2B && b2bData.auth_code) {
              let tokenData: AmosLoginData | null = null;

              try {
                const tokenResponse = await api.apiService.amos.tokenExchange(
                  b2bData.auth_code
                );
                if (typeof tokenResponse === 'string') {
                  throw new Error('Failed to exchange auth code');
                }
                tokenData = tokenResponse.data as AmosLoginData;
              } catch (tokenErr) {
                console.error('[loginAmos] Token exchange failed:', tokenErr);

                let mountUser = { isRegisteredAmos: true } as UserData;
                if (auth0User?.userInfo) {
                  mountUser = { ...auth0User.userInfo, ...mountUser };
                }
                setCookie('userAuth', JSON.stringify(mountUser));
                setUserData(mountUser);

                const existingToken = getCookie('amosToken');
                const existingRefresh = getCookie('amosRefreshToken');
                const existingClient = getCookie('amosClient');
                if (existingToken && existingToken !== '') {
                  setAmosToken(existingToken);
                  setAmosRefreshToken(existingRefresh);
                  setAmosClient(existingClient);
                }

                try {
                  const adultData = await fetchLoginB2BAdult();
                  if (adultData && adultData.isRegisteredB2B) {
                    setCookie('amosAdultClientToken', adultData.clientToken);
                    setCookie(
                      'amosAdultRefreshToken',
                      adultData.clientRefreshToken
                    );
                    setCookie('amosAdultClientId', adultData.clientId);
                    setCookie('amosAdultClientCode', '');
                    setAdultClientId(adultData.clientId || null);
                    setAdultClientToken(adultData.clientToken || null);
                    setAdultClientCode(null);
                    setAdultRefreshToken(adultData.clientRefreshToken || null);
                  }
                } catch (adultErr) {
                  console.error(
                    '[loginAmos] Adult login also failed:',
                    adultErr
                  );
                }

                setIsLoading(false);
                return;
              }

              if (tokenData && !('error' in tokenData)) {
                setCookie('amosToken', tokenData.clientToken || '');
                setCookie(
                  'amosRefreshToken',
                  tokenData.clientRefreshToken || ''
                );
                setCookie('amosClient', tokenData.clientId || '');
                setAmosToken(tokenData.clientToken || null);
                setAmosRefreshToken(tokenData.clientRefreshToken || null);
                setAmosClient(tokenData.clientId || null);

                const userEmail =
                  userData?.email || localStorage.getItem('lastUserEmail');
                if (
                  userEmail &&
                  tokenData.clientToken &&
                  tokenData.clientRefreshToken &&
                  tokenData.clientId
                ) {
                  try {
                    await offlineStorage.storeAmosToken(
                      userEmail,
                      tokenData.clientToken,
                      tokenData.clientRefreshToken,
                      tokenData.clientId
                    );
                  } catch (err) {
                    console.error(
                      '❌ Error storing AMOS token in IndexedDB:',
                      err
                    );
                  }
                }

                let mountUser = { isRegisteredAmos: true } as UserData;
                if (auth0User?.userInfo) {
                  mountUser = { ...auth0User.userInfo, ...mountUser };
                }
                setCookie('userAuth', JSON.stringify(mountUser));
                setUserData(mountUser);

                if (mountUser?.email && auth0User) {
                  try {
                    await offlineStorage.storeUserAuth(
                      mountUser.email,
                      mountUser,
                      auth0User
                    );
                    localStorage.setItem('lastUserEmail', mountUser.email);
                  } catch (err) {
                    console.error('❌ Error storing auth in IndexedDB:', err);
                  }
                }

                const adultData = await fetchLoginB2BAdult();
                if (adultData && adultData.isRegisteredB2B) {
                  setCookie('amosAdultClientToken', adultData.clientToken);
                  setCookie(
                    'amosAdultRefreshToken',
                    adultData.clientRefreshToken
                  );
                  setCookie('amosAdultClientId', adultData.clientId);
                  setCookie('amosAdultClientCode', '');

                  setAdultClientId(adultData.clientId || null);
                  setAdultClientToken(adultData.clientToken || null);
                  setAdultClientCode(null);
                  setAdultRefreshToken(adultData.clientRefreshToken || null);
                } else {
                  setCookie('amosAdultClientToken', '');
                  setCookie('amosAdultRefreshToken', '');
                  setCookie('amosAdultClientId', '');
                  setCookie('amosAdultClientCode', '');

                  setAdultClientId(null);
                  setAdultClientToken(null);
                  setAdultClientCode(null);
                  setAdultRefreshToken(null);
                }

                setIsLoading(false);
                return;
              }
            }

            if (!b2bData || !b2bData.isRegisteredB2B) {
              setCookie('auth_code', '');
              setCookie('amosToken', '');
              setCookie('amosRefreshToken', '');
              setCookie('amosClient', '');
              setAmosToken(null);
              setAmosRefreshToken(null);
              setAmosClient(null);

              let mountUser = { isRegisteredAmos: false } as UserData;
              if (auth0User?.userInfo) {
                mountUser = { ...auth0User.userInfo, ...mountUser };
              }
              setCookie('userAuth', JSON.stringify(mountUser));
              setUserData(mountUser);

              setIsLoading(false);
              return;
            }
          } catch (err) {
            console.error('[loginAmos] B2B login flow error:', err);
            let mountUser = { isRegisteredAmos: false } as UserData;
            if (auth0User?.userInfo) {
              mountUser = { ...auth0User.userInfo, ...mountUser };
            }
            setCookie('userAuth', JSON.stringify(mountUser));
            setUserData(mountUser);
            setIsLoading(false);
            return;
          }
        }

        const data = await fetchLogin();
        const adultData = await fetchLoginAdult();
        await updatePrimaryAmosSession(data);
        updateAdultAmosSession(adultData);

        const mountUser = buildMountUser(data);

        setCookie('userAuth', JSON.stringify(mountUser));
        setUserData(mountUser);

        await persistUserAuthOffline(mountUser);

        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.log(err);
      } finally {
        loginAmosInFlight.current = null;
      }
    })();

    loginAmosInFlight.current = loginPromise;
    return loginPromise;
  };

  const reFreshClientToken = async (): Promise<void> => {
    // If a token refresh is already running, share that promise so concurrent
    // callers (e.g. video + phone fetch both failing) do not each consume a
    // single-use refresh token.
    if (refreshTokenInFlight.current) {
      return refreshTokenInFlight.current;
    }

    const refreshPromise: Promise<void> = (async () => {
      try {
        setIsLoading(true);
        const response = await api.apiService.amos.amosRefreshToken(
          amosRefreshToken || ''
        );
        if (typeof response === 'string') {
          throw new Error('Failed to refresh token');
        }
        setCookie('amosToken', response.data.clientToken);
        setCookie('amosRefreshToken', response.data.clientRefreshToken);
        setCookie('amosClient', response.data.clientId);
        setAmosToken(response.data.clientToken);
        setAmosRefreshToken(response.data.clientRefreshToken);
        setAmosClient(response.data.clientId);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.log(err);
      } finally {
        refreshTokenInFlight.current = null;
      }
    })();

    refreshTokenInFlight.current = refreshPromise;
    return refreshPromise;
  };

  const reFreshAdultClientToken = async () => {
    try {
      setIsLoading(true);
      const response = await api.apiService.amos.amosRefreshToken(
        adultRefreshToken || ''
      );
      if (typeof response === 'string') {
        throw new Error('Failed to refresh adult token');
      }
      setCookie('amosAdultClientToken', response.data.clientToken);
      setCookie('amosAdultRefreshToken', response.data.clientRefreshToken);
      setCookie('amosAdultClientId', response.data.clientId);
      setAdultClientToken(response.data.clientToken);
      setAdultRefreshToken(response.data.clientRefreshToken);
      setAdultClientId(response.data.clientId);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log(err);
    }
  };

  const logoutAmos = async () => {
    try {
      setIsLoading(true);
      if (amosToken) {
        if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
          await api.apiService.amos.amosB2CLogout(amosToken);
        } else {
          await api.apiService.amos.amosLogout(amosToken);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setCookie('amosToken', '');
      setCookie('amosRefreshToken', '');
      setCookie('amosClient', '');
      setCookie('userAuth', '{}');
      setCookie('user', '');
      setAmosToken(null);
      setAmosRefreshToken(null);
      setAmosClient(null);
      setUserData(null);
      setAuth0User(null);
      clearListCookies();
      setIsLoading(false);
    }
  };

  const clearAmosToken = async () => {
    try {
      setIsLoading(true);
      if (amosToken) {
        await api.apiService.amos.amosLogout(amosToken);
      }
      setCookie('amosToken', '');
      setCookie('amosRefreshToken', '');
      setCookie('amosClient', '');
      setAmosToken(null);
      setAmosRefreshToken(null);
      setAmosClient(null);

      // Also clear amosToken from IndexedDB
      const userEmail = localStorage.getItem('lastUserEmail');
      if (userEmail) {
        try {
          await offlineStorage.deleteAmosToken(userEmail);
        } catch (err) {
          console.error('❌ Error clearing amos token from IndexedDB:', err);
        }
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log(err);
    }
  };

  const clearServiceWorkerCache = async (): Promise<boolean> => {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
        return true;
      }
      return false;
    } catch (err) {
      console.error('❌ Error clearing Service Worker cache:', err);
      return false;
    }
  };

  const fetchClientAndDependents = async () => {
    let token = getCookie('amosToken') || amosToken;
    let aclient = getCookie('amosClient') || amosClient;

    // If OFFLINE, try to load from IndexedDB first
    if (!navigator.onLine) {
      try {
        const userEmail = userData?.email || auth0User?.userInfo?.email;
        if (userEmail) {
          const cachedData = await offlineStorage.getClientData(userEmail);
          if (cachedData) {
            setClient(cachedData.client);
            setDependents(cachedData.dependents);
            setAdults(cachedData.adults);
            setIsLoading(false);
            return; // Exit early with cached data
          } else {
            console.log('❌ No cached client data in IndexedDB');
          }
        }
      } catch (err) {
        console.error('❌ Error loading from IndexedDB:', err);
      }
      setIsLoading(false);
      return; // Exit if offline and no cache
    }

    // ONLINE: Proceed with normal flow
    // If no token available, try to login first
    if (!token || token === '' || token === 'null') {
      try {
        await loginAmos();
        // After login, get the tokens again
        token = getCookie('amosToken');
        aclient = getCookie('amosClient');
      } catch (err) {
        console.error('❌ Login failed:', err);
        setIsLoading(false);
        return; // Exit if login fails
      }
    }

    // If still no token after login attempt, exit
    if (!token || token === '' || token === 'null') {
      console.error('❌ Cannot fetch client data - no token available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      let data;
      if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
        const response = await api.apiService.amos.getB2BClientAndDependents(
          token,
          aclient || ''
        );
        if (typeof response === 'string') {
          throw new Error('Failed to fetch b2b client data');
        }
        data = response.data;
        await fetchB2BAdults();
      } else {
        const response = await api.apiService.amos.getClientAndDependents(
          token,
          aclient || ''
        );
        if (typeof response === 'string') {
          throw new Error('Failed to fetch client data');
        }
        data = response.data;
        await fetchAdults();
      }

      const clientData: ClientData = { ...data };
      delete clientData.dependents;
      clientData.type = 'CL';

      setDependents(data.dependents);
      setClient(clientData);

      // Store in IndexedDB for offline use
      const userEmail = userData?.email || auth0User?.userInfo?.email;
      if (userEmail) {
        try {
          await offlineStorage.storeClientData(
            userEmail,
            clientData,
            data.dependents,
            adults
          );
        } catch (err) {
          console.error('⚠️ Failed to cache client data:', err);
        }
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.error('❌ Error fetching client and dependents:', err);
    }
  };

  const isValid = (value: any): boolean =>
    value !== null && value !== undefined && value !== '' && value !== 'null';

  const fetchAdults = async () => {
    // Get tokens from cookies or state
    const adultToken = getCookie('amosAdultClientToken') || adultClientToken;
    const adultId = getCookie('amosAdultClientId') || adultClientId;

    if (isValid(adultToken) && isValid(adultId) && adultToken && adultId) {
      try {
        setIsLoading(true);
        const response = await api.apiService.amos.getAdultDetails(
          adultToken || '',
          adultId || ''
        );
        if (typeof response === 'string') {
          throw new Error('Failed to fetch adult details');
        }
        delete response.data.dependents;
        response.data.type = 'AB';
        setAdults(response.data);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error('❌ Error fetching adult details:', err);
      }
    } else {
      console.log(
        'ℹ️ No adult beneficiary token/ID available - skipping fetch'
      );
    }
  };

  const fetchB2BAdults = async () => {
    const adultToken = getCookie('amosAdultClientToken') || adultClientToken;
    const adultId = getCookie('amosAdultClientId') || adultClientId;

    if (isValid(adultToken) && isValid(adultId)) {
      try {
        setIsLoading(true);
        const response = await api.apiService.amos.getB2BAdultDetails(
          adultToken || '',
          adultId || ''
        );
        if (typeof response === 'string') {
          throw new Error('Failed to fetch b2b adult details');
        }
        const { data } = response;
        delete data.dependents;
        data.type = 'AB';
        setAdults(data);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error('Error fetching b2b adult details:', err);
      }
    } else {
      console.log(
        'No b2b adult beneficiary token/ID available - skipping fetch'
      );
    }
  };

  const fetchAmosCountry = async (idiom: string) => {
    try {
      setIsLoading(true);
      const response = await api.apiService.amos.getCountries(idiom);
      if (typeof response === 'string') {
        throw new Error('Failed to fetch countries');
      }

      const _country: CountryOption[] = response.data.map((el: any) => ({
        label: el.value,
        value: el.key,
      }));

      // Sort and unique array items because same phone code for countries
      const _countryDdi: CountryDdiOption[] = getUniqueArrayBy(
        response.data.map((el: any) => ({
          label: `+${el.phoneCode}`,
          value: el.phoneCode,
          onlySort: el.phoneCode,
        })),
        'onlySort'
      );

      setCountryDdi(
        _countryDdi.sort((a, b) => Number(a.onlySort) - Number(b.onlySort))
      );
      setCountry(_country);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log(err);
    }
  };

  return (
    <GeneralContext.Provider
      value={{
        userData,
        amosToken,
        amosRefreshToken,
        amosClient,
        isLogged,
        clientCode,
        client,
        dependents,
        refreshClientAndDependents,
        country,
        countryDdi,
        fetchAmosCountry,
        fetchClientAndDependents,
        setRefreshClientAndDependents,
        loginAmos,
        logoutAmos,
        setIsLoading,
        clearAmosToken,
        clearServiceWorkerCache,
        setUserData,
        reFreshClientToken,
        ispopup,
        setIspopup,
        adults,
        adultClientId,
        adultClientToken,
        adultClientCode,
        adultRefreshToken,
        fetchAdults,
        reFreshAdultClientToken,
      }}
    >
      <Loading active={isLoading} />
      {children}
    </GeneralContext.Provider>
  );
}
